import { Link } from "@tanstack/react-router";
import { ArrowRight, MapPin, Sparkles } from "lucide-react";

/** Location-specific anchors so every page rendering this CTA links to a city page. */
const CITY_ANCHORS = [
  { to: "/louvered-patio-covers/in/san-antonio" as const, label: "Louvered patio covers in San Antonio, TX" },
  { to: "/louvered-patio-covers/in/austin" as const, label: "Louvered patio covers in Austin, TX" },
];
const PERGOLA_CITIES = ["new-braunfels", "seguin", "boerne", "san-marcos"] as const;
const PERGOLA_CITY_NAMES: Record<string, string> = {
  "new-braunfels": "New Braunfels",
  seguin: "Seguin",
  boerne: "Boerne",
  "san-marcos": "San Marcos",
};

/**
 * Prominent cross-link banner pointing awnings / patio-cover / shade pages
 * to the /louvered-pergolas hub. Drop into ServicePage `postCta` or render
 * directly in custom layouts.
 */
export function PergolaUpgradeCta() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-navy-deep via-navy-deep to-red/20 py-14 text-white sm:py-20">
      <div
        className="pointer-events-none absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0, transparent 50%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-8 px-4 text-center lg:flex-row lg:text-left lg:px-8">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-red/60 bg-white/5">
          <Sparkles className="h-8 w-8 text-red" />
        </div>
        <div className="flex-1">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
            ★ Upgrade Your Outdoor Room
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold leading-tight sm:text-4xl">
            Ready for a Permanent Louvered Pergola?
          </h2>
          <p className="mt-3 max-w-2xl text-base text-white/80 sm:text-lg">
            A motorized louvered pergola is the next step up from a fabric awning —
            rotating aluminum roof louvers, integrated gutters, motorized screens,
            Bosso lighting, fans and smart automation. Engineered, installed and
            financed through Acorn Finance. Explore our louvered pergola hub.
          </p>
        </div>
        <Link
          to="/louvered-pergolas"
          className="group inline-flex shrink-0 items-center gap-2 bg-red px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-white shadow-bold transition hover:bg-white hover:text-red"
        >
          Explore Louvered Pergolas
          <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
        </Link>
      </div>

      <nav
        aria-label="Louvered pergola city pages"
        className="relative mx-auto mt-8 flex max-w-6xl flex-wrap items-center justify-center gap-2 px-4 lg:justify-start lg:px-8"
      >
        {CITY_ANCHORS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="inline-flex items-center gap-2 border border-white/25 bg-white/5 px-4 py-2 font-display text-xs font-semibold uppercase tracking-wider text-white transition hover:border-red hover:bg-red"
          >
            <MapPin className="h-3.5 w-3.5 text-red" aria-hidden="true" />
            {c.label}
          </Link>
        ))}
        {PERGOLA_CITIES.map((slug) => (
          <Link
            key={slug}
            to="/pergola-covers/in/$city"
            params={{ city: slug }}
            className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-display text-xs font-semibold uppercase tracking-wider text-white/85 transition hover:border-red hover:bg-red hover:text-white"
          >
            <MapPin className="h-3.5 w-3.5 text-red" aria-hidden="true" />
            Patio &amp; pergola covers in {PERGOLA_CITY_NAMES[slug]}, TX
          </Link>
        ))}
      </nav>
    </section>
  );
}
